/* eslint-disable max-lines */
/* eslint-disable max-len */

import { ArrowRightIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import { format, parse } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useDashboards from "../../hooks/useDashboards";
import useFetch from "../../hooks/useFetch";
import { TripSalesType } from "../../interfaces/endpoints/IAnalytics.types";
import { getOperatorTripsSalesAnalytics } from "../../services/Analytics.service";
import { cx } from "../../utils/classnames";
import { serialize } from "../../utils/serializeToQueryString";
import PdfIcon from "../shared/svgs/PdfIcon";
import UserIcon from "../shared/svgs/User";
import SpinnerLoader from "../ui/SpinnerLoader";

const TripsSalesAnalytics = () => {
  const { line, from, to } = useDashboards();
  const [expandedLines, setExpandedLines] = useState<Set<number>>(new Set());

  const { response, loading, handleFunc } = useFetch(
    getOperatorTripsSalesAnalytics,
    { loading: true, resetResponseOnError: true }
  );

  useEffect(() => {
    if (from && to) {
      void handleFunc({
        startDate: format(parse(from, "dd/MM/yyyy", new Date()), "MM-dd-yyyy"),
        endDate: format(parse(to, "dd/MM/yyyy", new Date()), "MM-dd-yyyy"),
        lineId: line?.value || undefined
      });
    }
  }, [from, to, line]);

  const navigate = useNavigate();

  const toggleLineExpansion = (lineId: number) => {
    setExpandedLines((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(lineId)) {
        newSet.delete(lineId);
      } else {
        newSet.add(lineId);
      }
      return newSet;
    });
  };

  const onClick = (trip: TripSalesType) => {
    if (from && to && !!trip.nrOfPassengers)
      navigate(
        `/trips-sales/${trip.tripId}${serialize({
          df: format(parse(from, "dd/MM/yyyy", new Date()), "yyyy-MM-dd"),
          dt: format(parse(to, "dd/MM/yyyy", new Date()), "yyyy-MM-dd")
        })}`
      );
  };

  const _onClick = () => {
    if (from && to)
      navigate(
        `/line-sales${serialize({
          df: format(parse(from, "dd/MM/yyyy", new Date()), "yyyy-MM-dd"),
          dt: format(parse(to, "dd/MM/yyyy", new Date()), "yyyy-MM-dd")
        })}`
      );
  };

  const totalPassengers =
    response?.data.reduce(
      (total, p) =>
        total +
        p.tripSales.reduce(
          (tripTotal, c) => tripTotal + (c.nrOfPassengers || 0),
          0
        ),
      0
    ) || 0;

  const processedData =
    response?.data
      .map((lineData) => {
        const totalLinePassengers = lineData.tripSales.reduce(
          (total, trip) => total + (trip.nrOfPassengers || 0),
          0
        );
        const totalLineSales = lineData.tripSales.reduce(
          (total, trip) => total + trip.totalSales,
          0
        );
        const sortedTrips = [...lineData.tripSales].sort(
          (a, b) => (b.nrOfPassengers || 0) - (a.nrOfPassengers || 0)
        );

        return {
          ...lineData,
          totalLinePassengers,
          totalLineSales,
          sortedTrips
        };
      })
      .sort((a, b) => b.totalLinePassengers - a.totalLinePassengers) || [];

  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between items-center pb-1.5">
        <p className="font-medium leading-6">{processedData.length}{" "}
          {processedData.length === 1 ? "Linjë" : "Linja"}</p>
        {totalPassengers > 0 && (
          <div className="flex gap-2 cursor-pointer items-center">
            <p
              onClick={() => _onClick()}
              className="bg-transparent text-primary text-sm font-medium leading-[120%]"
            >
              Shkarko të gjitha
            </p>
            <PdfIcon className="text-primary w-5 h-5" />
          </div>
        )}
      </div>
      <p className="text-[#767676] font-medium block text-xs leading-[120%] whitespace-nowrap pb-3">
        {totalPassengers.toLocaleString()} pasagjerë
      </p>
      <div className="overflow-hidden elative ">
        <div>
          {processedData.length && !loading ? (
            <div className="block h-[310px] overflow-auto ">
              {processedData.map((lineData) => (
                <>
                  <div
                    key={`line-${lineData.lineId}`}
                    className={cx([
                      "cursor-pointer border rounded-lg mb-2 last:mb-0 overflow-hidden",
                      expandedLines.has(lineData.lineId)
                        ? "border-primary"
                        : "border-[#eaeaea] "
                    ])}
                    style={{ tableLayout: "fixed" }}
                    onClick={() => toggleLineExpansion(lineData.lineId)}
                  >
                    <div className="flex items-center w-full justify-between">
                      <div className="flex items-center w-full py-3.5 pl-2.5 hover:bg-[#FCFCFC]/80">
                        <div className="flex items-center gap-2">
                          <p className="truncate font-medium text-left whitespace-nowrap">
                            {lineData.lineName.split("-")[0]?.trim()}
                          </p>
                          <ArrowRightIcon className="text-black w-4 h-4" />
                          <p className="truncate font-medium text-left whitespace-nowrap">
                            {lineData.lineName.split("-")[1]?.trim()}
                          </p>
                        </div>
                        <span className="h-2.5 border-r border-[#2c2c2c] pr-3 mr-3"></span>
                        <p className="text-sm font-medium flex items-center">
                          <UserIcon className="w-4 h-4 mr-1" />
                          {lineData.totalLinePassengers.toLocaleString()}
                        </p>
                        <span className="h-2.5 border-r border-[#2c2c2c] pr-3 mr-3"></span>
                        <p className="text-sm font-semibold whitespace-nowrap">
                          {lineData.totalLineSales.toLocaleString()} €
                        </p>
                        <span className="h-2.5 border-r border-[#2c2c2c] pr-3 mr-3"></span>
                        <p
                          className="relative z-10 cursor-pointer w-5 h-5"
                          onClick={() => {
                            if (from && to && lineData.totalLinePassengers > 0)
                              navigate(
                                `/line-sales${serialize({
                                  lineId: lineData?.lineId,
                                  df: format(
                                    parse(from, "dd/MM/yyyy", new Date()),
                                    "yyyy-MM-dd"
                                  ),
                                  dt: format(
                                    parse(to, "dd/MM/yyyy", new Date()),
                                    "yyyy-MM-dd"
                                  )
                                })}`
                              );
                          }}
                        >
                          <PdfIcon className="text-primary" />
                        </p>
                      </div>
                      <ChevronDownIcon
                        className={cx([
                          "transition-transform duration-200 w-5 h-5 mr-3",
                          expandedLines.has(lineData.lineId)
                            ? "rotate-180"
                            : "rotate-0"
                        ])}
                      />
                    </div>
                    {expandedLines.has(lineData.lineId) && (
                      <table className="w-full bg-[#FCFCFC]">
                        <thead className="border-b">
                          <tr>
                            <th className="p-3 text-left text-xs font-medium text-[#767676] w-[100px]">
                              Oraret
                            </th>
                            <th className="p-3 text-right text-xs font-medium text-[#767676] w-[90px]">
                              Pasagjerët
                            </th>
                            <th className="p-3 text-right text-xs font-medium text-[#767676] w-[90px]">
                              Shitjet
                            </th>
                            <th className="p-3 text-right text-xs font-medium text-[#767676] w-[90px]">
                              PDF
                            </th>
                          </tr>
                        </thead>
                        <tbody className="">
                          {lineData.sortedTrips.map((trip) => (
                            <tr
                              key={`trip-${trip.tripId}`}
                              className={cx([
                                "bg-[#FCFCFC] border-b last:border-b-0",
                                !!trip.nrOfPassengers && "cursor-pointer"
                              ])}
                              onClick={() => onClick(trip)}
                            >
                              <td className="p-3 text-left text-sm whitespace-nowrap w-[100px]">
                                <div className="flex items-center gap-1">
                                  <p className="text-sm leading-[120%]">
                                    {trip.departureTime.slice(0, 5)}
                                  </p>
                                  <ArrowRightIcon className="text-[#767676] w-4 h-4" />
                                  <p className="text-sm leading-[120%]">
                                    {trip.arrivalTime.slice(0, 5)}
                                  </p>
                                </div>
                              </td>
                              <td className="p-3 text-right w-[90px]">
                                <p className="text-sm leading-[120%] flex items-center justify-end">
                                  <UserIcon className="w-4 h-4 mr-1" />
                                  {trip.nrOfPassengers.toLocaleString()}
                                </p>
                              </td>
                              <td className="p-3 text-right text-sm whitespace-nowrap w-[90px]">
                                <p className="text-sm leading-[120%]">
                                  {trip.totalSales.toLocaleString()} €
                                </p>
                              </td>
                              <td className="p-3 text-sm w-[90px]">
                                <div className="flex items-center w-full justify-end text-right">
                                  <PdfIcon className="text-primary w-5 h-5" />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              ))}
            </div>
          ) : null}
        </div>
        {!processedData.length && !loading ? (
          <div className=" mx-6 mb-2 flex-col items-center mt-6 block h-[310px]">
            <p className="text-neutral text-sm">Nuk keni asnje te dhene!</p>
            <p className="text-neutral text-xs">
              Per linjen e caktuar dhe data e zgjedhuar.
            </p>
          </div>
        ) : null}
        {loading ? (
          <SpinnerLoader className="text-primary absolute right-0 left-0 top-16" />
        ) : null}
      </div>
    </div>
  );
};

export default TripsSalesAnalytics;
